import { ReactNode, useState, useRef, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Shield, Moon, Sun, LogOut, User as UserIcon, ChevronDown, Menu, X, BookOpen } from 'lucide-react'
import { useTheme } from '@/shared/contexts/ThemeContext'
import { useAuth } from '@/shared/contexts/AuthContext'
import { APP_VERSION } from '@/shared/lib/constants'

interface HeaderProps {
  /** Acciones extra a la derecha (botones, links). Se renderizan ANTES del theme toggle y user menu. */
  actions?: ReactNode
  /** Si true oculta el toggle de tema (cuando ya viene incluido en `actions`). */
  hideThemeToggle?: boolean
  /** Si true oculta el user menu / login button. */
  hideAuthControls?: boolean
}

/**
 * Header compartido del ecosistema SignWiz. Patrón inspirado en Maskedata:
 * - Sticky top, blur backdrop
 * - Logo + brand + version + tagline a la izquierda (clic vuelve a /)
 * - Acciones a la derecha: actions slot + theme toggle + auth controls
 *
 * Auth controls:
 * - Logueado: avatar con email + dropdown (cerrar sesión)
 * - No logueado: botón "Ingresar"
 */
export function Header({ actions, hideThemeToggle = false, hideAuthControls = false }: HeaderProps) {
  const navigate = useNavigate()
  const { theme, toggleTheme } = useTheme()
  const { user, signOut } = useAuth()
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const isDark = theme === 'dark'

  useEffect(() => {
    if (!menuOpen) return
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [menuOpen])

  const handleSignOut = async () => {
    setMenuOpen(false)
    await signOut()
    navigate('/login', { replace: true })
  }

  return (
    <header className="bg-white/80 dark:bg-legal-800/80 backdrop-blur-sm border-b border-legal-200 dark:border-legal-700 sticky top-0 z-40">
      <div className="px-3 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center justify-between gap-2">
          <Link
            to="/"
            className="flex items-center gap-2 sm:gap-3 hover:opacity-80 transition-opacity min-w-0"
          >
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-primary-600 dark:bg-primary-500 flex items-center justify-center flex-shrink-0">
              <Shield className="text-white w-5 h-5 sm:w-6 sm:h-6" />
            </div>
            <div className="text-left min-w-0">
              <div className="flex items-baseline gap-1.5">
                <h1 className="text-lg sm:text-xl font-bold truncate">
                  <span className="text-legal-900 dark:text-white">Sign</span>
                  <span className="text-primary-600 dark:text-primary-400">Wiz</span>
                </h1>
                <span className="text-[10px] sm:text-xs font-medium text-legal-400 dark:text-legal-500 flex-shrink-0">
                  v{APP_VERSION}
                </span>
              </div>
              <p className="hidden sm:block text-xs text-legal-500 dark:text-legal-400 text-left truncate">
                Evidencia Digital Argentina
              </p>
            </div>
          </Link>

          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            <Link
              to="/blog"
              className="hidden sm:flex items-center gap-1.5 px-3 py-2 min-h-10 text-sm text-legal-600 dark:text-legal-300 hover:text-legal-900 dark:hover:text-white hover:bg-legal-100 dark:hover:bg-legal-700 rounded-lg transition-colors"
            >
              <BookOpen size={16} />
              <span>Blog</span>
            </Link>

            {actions}

            {!hideThemeToggle && (
              <button
                onClick={toggleTheme}
                className="p-2 min-w-10 min-h-10 flex items-center justify-center rounded-lg text-legal-700 dark:text-legal-300 hover:bg-legal-100 dark:hover:bg-legal-700 transition-colors"
                aria-label={isDark ? 'Tema claro' : 'Tema oscuro'}
              >
                {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
            )}

            {!hideAuthControls && (
              user ? (
                <div ref={menuRef} className="relative">
                  <button
                    onClick={() => setMenuOpen(o => !o)}
                    className="flex items-center gap-2 px-2 sm:px-3 py-2 min-h-10 text-sm text-legal-700 dark:text-legal-200 hover:bg-legal-100 dark:hover:bg-legal-700 rounded-lg transition-colors"
                  >
                    <div className="w-7 h-7 rounded-full bg-primary-600 dark:bg-primary-500 text-white flex items-center justify-center text-xs font-bold">
                      {(user.email || '?').charAt(0).toUpperCase()}
                    </div>
                    <span className="hidden md:inline max-w-[140px] truncate">{user.email}</span>
                    <ChevronDown size={14} className="hidden sm:inline" />
                  </button>
                  {menuOpen && (
                    <div className="absolute right-0 top-full mt-2 w-64 bg-white dark:bg-legal-800 border border-legal-200 dark:border-legal-700 rounded-lg shadow-lg py-2 z-50">
                      <div className="px-4 py-2 border-b border-legal-100 dark:border-legal-700">
                        <p className="text-xs text-legal-500 dark:text-legal-400">Sesión iniciada como</p>
                        <p className="text-sm font-medium text-legal-900 dark:text-white truncate">{user.email}</p>
                      </div>
                      <Link
                        to="/dashboard"
                        onClick={() => setMenuOpen(false)}
                        className="block px-4 py-2 text-sm text-legal-700 dark:text-legal-200 hover:bg-legal-50 dark:hover:bg-legal-700"
                      >
                        Dashboard
                      </Link>
                      <button
                        onClick={handleSignOut}
                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                      >
                        <LogOut size={16} />
                        Cerrar sesión
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  to="/login"
                  className="flex items-center gap-2 px-3 sm:px-4 py-2 min-h-10 text-sm bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors"
                >
                  <UserIcon size={16} />
                  <span>Ingresar</span>
                </Link>
              )
            )}
          </div>
        </div>
      </div>
    </header>
  )
}

/**
 * Botón de menú mobile (hamburguesa) — útil en pages con sidebar para
 * que en mobile se pueda toggle el sidebar via Header.
 */
export function MobileMenuToggle({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  return (
    <button
      onClick={onToggle}
      className="lg:hidden p-2 min-w-10 min-h-10 flex items-center justify-center rounded-lg text-legal-700 dark:text-legal-300 hover:bg-legal-100 dark:hover:bg-legal-700 transition-colors"
      aria-label={open ? 'Cerrar menú' : 'Abrir menú'}
    >
      {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
    </button>
  )
}
